import { useCallback, useEffect, useSyncExternalStore } from "react";
import * as codeModeStore from "./store";
import type { CodeModeEntry } from "./table";
import { usePptSelectionKind } from "../features/action-chips/hooks/usePptSelectionKind";

export interface UseCodeModeResult {
  readonly handleKeyDown: (event: KeyboardEvent) => boolean;
}

/**
 * React binding for the Code Mode accumulator (codeMode/store.ts). The store
 * is plain module state, not React state — it must stay reachable from the
 * Office.actions.associate handlers in codeMode/commands.ts, which can fire
 * from the slide before DeckorateModule has ever mounted — so this hook only
 * subscribes and feeds it selection state and in-pane keystrokes; it never
 * owns the accumulator's state itself.
 *
 * The snapshot is subscribed to but not returned: code mode runs with no UI
 * of its own, so nothing renders it. The subscription is still what drives
 * the dispatch effect below when a code resolves.
 */
export function useCodeMode(onRun: (entry: CodeModeEntry) => void): UseCodeModeResult {
  const snapshot = useSyncExternalStore(codeModeStore.subscribe, codeModeStore.getSnapshot);
  const selection = usePptSelectionKind();

  useEffect(() => {
    codeModeStore.setSelectionActive(selection.shapeCount > 0);
  }, [selection.shapeCount]);

  useEffect(() => {
    if (!snapshot.resolved) return;
    const entry = codeModeStore.consumeResolved();
    if (entry) onRun(entry);
  }, [snapshot.resolved, onRun]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => codeModeStore.handleKeydown(event), []);

  return { handleKeyDown };
}
